/**
 * PerfectDiffuserPage.tsx — Adjustable Perfect Diffuser specification for
 *                            Barcombe, with annual / seasonal performance,
 *                            automatic placement on basin bathymetry, and
 *                            adjustment-feedback engine.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Wrench, ArrowLeft, Sparkles, Layers, Activity, ZapOff, Zap, TrendingUp,
  CheckCircle2, AlertTriangle, XCircle, Save, ChevronRight, Settings2, Wind,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  PERFECT_DIFFUSER_DEFAULT, computeDevicePerformance, evaluateAnnual,
  generateAdjustments, autoPlaceDevices,
  SEASON_LABEL, type PerfectDiffuserSpec, type Season,
} from '@/lib/perfectDiffuser';
import { saveCustomScenario, makeCustomScenario, activateScenario } from '@/lib/scenarioManager';
import type { SiteBathymetry } from '@/lib/benchmarkSites';
import BathymetryMap, { type PlacedModule } from '@/components/BathymetryMap';
import ReservoirHealthScorecard from '@/components/ReservoirHealthScorecard';

const BARCOMBE_BATH: SiteBathymetry = {
  shape: 'man-made-reservoir',
  extent_m: { width: 260, height: 180 },
  outline: [
    [0.05, 0.45], [0.10, 0.28], [0.22, 0.18], [0.40, 0.13], [0.60, 0.12],
    [0.78, 0.18], [0.90, 0.32], [0.95, 0.50], [0.92, 0.68], [0.82, 0.82],
    [0.62, 0.90], [0.42, 0.91], [0.24, 0.86], [0.12, 0.74], [0.05, 0.60],
  ],
  deepest: [0.55, 0.55],
  hypsometricP: 1.3,
  source: 'South East Water bathymetric survey 2022 (modelled approximation)',
  gridN: 35,
};

const STORAGE_KEY = 'rb_perfect_diffuser_spec_v1';

function loadSavedSpec(): PerfectDiffuserSpec {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...PERFECT_DIFFUSER_DEFAULT, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return PERFECT_DIFFUSER_DEFAULT;
}

export default function PerfectDiffuserPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<PerfectDiffuserSpec>(loadSavedSpec);

  const upd = <K extends keyof PerfectDiffuserSpec>(k: K, v: PerfectDiffuserSpec[K]) =>
    setSpec(s => ({ ...s, [k]: v }));

  const annual = useMemo(() => evaluateAnnual(spec), [spec]);
  const adjustments = useMemo(() => generateAdjustments(spec), [spec]);
  const placedDevices = useMemo(() => autoPlaceDevices(spec, BARCOMBE_BATH, 8), [spec]);

  // Convert placed devices to PlacedModule[] for BathymetryMap (display only)
  // MUST be memoised — passing a fresh array reference each render combines
  // with BathymetryMap's initialModules useEffect to create an infinite render
  // loop, choking the main thread and making the sidebar/buttons unresponsive.
  const placedForMap: PlacedModule[] = useMemo(
    () => placedDevices.map(d => ({ id: d.id, x: d.x, y: d.y, depth: d.depth_m })),
    [placedDevices]
  );

  const annualColour = annual.annualStatus === 'ok' ? 'border-emerald-700 bg-emerald-900/15 text-emerald-100'
                     : annual.annualStatus === 'warn' ? 'border-amber-700 bg-amber-900/15 text-amber-100'
                     : 'border-red-700 bg-red-900/15 text-red-100';

  const saveSpec = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(spec));
    alert('Perfect Diffuser spec saved to this browser. Use "Load to Barcombe" to push the matching scenario into the simulator.');
  };

  const loadToBarcombe = (season: Season = 'summer') => {
    const result = annual.seasons.find(r => r.season === season);
    if (!result) return;
    const scenarioName = `Barcombe — Perfect Diffuser (${season})`;
    const saved = makeCustomScenario(scenarioName, result.inputs, {
      trainId: 'shallow_uk_eutrophic',
      description: `Active Perfect-Diffuser configuration: ${spec.count} × ${spec.membrane} discs @ ${spec.Qair_perDevice_Ls} L/s, ${spec.releaseDepth_m} m depth, ${spec.bubble_d_mm} mm bubble, ${spec.layout} layout, ${spec.dutyMode} duty.`,
    });
    saveCustomScenario(saved);
    activateScenario(saved);
    navigate(`/train-summary/shallow_uk_eutrophic`);
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Wrench} title="Perfect Diffuser — Barcombe Aeration Device"
        subtitle="Fully adjustable fine-bubble device specification with array placement, annual seasonal performance and adjustment feedback"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={saveSpec}
              className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 px-3 py-1.5 text-sm font-semibold text-white">
              <Save className="w-4 h-4" /> Save spec
            </button>
            <button onClick={() => loadToBarcombe('summer')}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white">
              <Sparkles className="w-4 h-4" /> Load to Barcombe scenario
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">What this page does:</strong> defines the canonical Reynolds &amp; Bauhm "Perfect Diffuser"
        device for shallow eutrophic UK reservoirs. Every design parameter is adjustable below; the simulator computes annual
        performance across four seasons (winter / spring / summer / autumn) and reports the resulting reservoir health for each.
        Devices are auto-placed on the basin bathymetry with deep-zone bias. Adjustment feedback at the bottom of the page
        identifies the single change that would produce the largest health gain.
      </div>

      {/* ─────────── Annual headline ─────────── */}
      <section className={`rounded-2xl border-2 p-4 ${annualColour}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              <h2 className="text-lg font-bold">Annual performance — Barcombe (all 4 seasons)</h2>
            </div>
            <div className="text-xs opacity-90 mt-1">
              Weighted mean across winter, spring, summer, autumn. Worst season: <strong>{SEASON_LABEL[annual.worstSeason]}</strong>.
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{annual.annualScore.toFixed(0)}</div>
            <div className="text-[10px] uppercase tracking-wider">/ 100 ANNUAL HEALTH</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {annual.seasons.map(r => (
            <div key={r.season} className="rounded-lg border border-slate-700/70 bg-slate-950/50 p-2">
              <div className="text-xs font-bold text-slate-100">{SEASON_LABEL[r.season]}</div>
              <div className="text-2xl font-bold mt-1"
                style={{ color: r.health.overallStatus === 'ok' ? '#34d399' : r.health.overallStatus === 'warn' ? '#fbbf24' : '#f87171' }}>
                {r.health.overallScore.toFixed(0)}
              </div>
              <div className="text-[10px] text-slate-500 uppercase">
                {r.health.overallStatus === 'ok' ? 'healthy' : r.health.overallStatus === 'warn' ? 'at risk' : 'critical'}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── Device adjustability ─────────── */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-sky-300" /> Device design (per unit)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SliderField label="Bubble release diameter" unit="mm" min={0.5} max={5.0} step={0.1}
            value={spec.bubble_d_mm} onChange={v => upd('bubble_d_mm', v)}
            help="Fine-bubble 1-3 mm gives best OTE. Sub-mm fouls quickly." />
          <SliderField label="Pore size" unit="µm" min={300} max={3000} step={50}
            value={spec.poreSize_um} onChange={v => upd('poreSize_um', v)}
            help="EPDM membrane typical 1500 µm." />
          <SelectField<'EPDM' | 'silicone' | 'ceramic-dome'> label="Membrane material"
            value={spec.membrane}
            options={[
              ['EPDM', 'EPDM (flexible, 10-yr life)'],
              ['silicone', 'Silicone (12-yr life, premium)'],
              ['ceramic-dome', 'Ceramic dome (rigid, 20-yr life)'],
            ]}
            onChange={v => upd('membrane', v)} />
          <SliderField label="Disc diameter" unit="mm" min={150} max={500} step={10}
            value={spec.discDiameter_mm} onChange={v => upd('discDiameter_mm', v)} />
          <SliderField label="Air per device" unit="L/s" min={0.3} max={6.0} step={0.1}
            value={spec.Qair_perDevice_Ls} onChange={v => upd('Qair_perDevice_Ls', v)}
            help="2.0 L/s = sweet spot for fine-bubble OTE." />
          <SliderField label="Release depth (below surface)" unit="m" min={1.0} max={7.5} step={0.1}
            value={spec.releaseDepth_m} onChange={v => upd('releaseDepth_m', v)} />
          <SliderField label="Height above bed" unit="m" min={0.1} max={3.0} step={0.1}
            value={spec.heightAboveBed_m} onChange={v => upd('heightAboveBed_m', v)}
            help="0.5 m balances OTE and bed-scour risk." />
          <SliderField label="CAPEX per device" unit="£" min={500} max={6000} step={100}
            value={spec.capexPerDevice} onChange={v => upd('capexPerDevice', v)} />
        </div>
      </section>

      {/* ─────────── Array adjustability ─────────── */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-emerald-300" /> Array configuration
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SliderField label="Number of devices" unit="" min={4} max={48} step={1}
            value={spec.count} onChange={v => upd('count', v)}
            help="14-18 typical for Barcombe footprint." />
          <SelectField<'hex' | 'square' | 'line' | 'deep-cluster'> label="Layout pattern"
            value={spec.layout}
            options={[
              ['hex', 'Hexagonal staggered grid'],
              ['square', 'Square grid'],
              ['line', 'Single line (perimeter or central)'],
              ['deep-cluster', 'Cluster in deepest zone'],
            ]}
            onChange={v => upd('layout', v)} />
          <SliderField label="Spacing factor" unit="× plume Ø" min={1.0} max={3.0} step={0.1}
            value={spec.spacingFactor} onChange={v => upd('spacingFactor', v)}
            help="1.8 → Asaeda Type-1 regime." />
          <SliderField label="Deep-zone bias" unit="0-1" min={0} max={1} step={0.05}
            value={spec.deepZoneBias} onChange={v => upd('deepZoneBias', v)}
            help="1.0 = all devices in deepest 30% cells." />
          <SelectField<'continuous' | 'day-only' | 'seasonal' | 'pulsed-30min'> label="Duty mode"
            value={spec.dutyMode}
            options={[
              ['continuous', 'Continuous (24/7)'],
              ['day-only', 'Daylight only (12/24)'],
              ['pulsed-30min', 'Pulsed 30-min on/off'],
              ['seasonal', 'Seasonal (manual %)'],
            ]}
            onChange={v => upd('dutyMode', v)} />
          {spec.dutyMode === 'seasonal' && (
            <SliderField label="Duty cycle" unit="%" min={5} max={100} step={5}
              value={spec.dutyPercent} onChange={v => upd('dutyPercent', v)} />
          )}
        </div>
      </section>

      {/* ─────────── Computed performance ─────────── */}
      <section className="rounded-2xl border border-sky-700/40 bg-sky-950/15 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Wind className="w-4 h-4 text-sky-300" /> Performance — computed
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PerfCard k="Effective OTE"        v={`${annual.perDevice.ote_pct.toFixed(0)} %`} t="> 25 %" ok={annual.perDevice.ote_pct > 25} />
          <PerfCard k="Plume diameter"       v={`${annual.perDevice.plumeDiameter_m.toFixed(1)} m`} t="—" ok />
          <PerfCard k="O₂ per device"        v={`${annual.perDevice.o2PerDevice_kgDay.toFixed(1)} kg/d`} t="—" ok />
          <PerfCard k="Array O₂ total"       v={`${annual.perDevice.o2Total_kgDay.toFixed(0)} kg/d`} t="—" ok />
          <PerfCard k="Power per device"     v={`${annual.perDevice.power_kW.toFixed(2)} kW`} t="—" ok />
          <PerfCard k="Array total power"    v={`${annual.perDevice.powerTotal_kW.toFixed(1)} kW`} t="—" ok />
          <PerfCard k="£ per kg O₂"          v={`£ ${annual.perDevice.costPerKgO2.toFixed(2)}`} t="< £1.5"
            ok={annual.perDevice.costPerKgO2 < 1.5} />
          <PerfCard k="Plume coverage"       v={`${(annual.perDevice.coverageFraction * 100).toFixed(0)} %`} t="> 80 %"
            ok={annual.perDevice.coverageFraction > 0.8} />
          <PerfCard k="CAPEX (array)"        v={`£ ${(annual.perDevice.capex_GBP / 1000).toFixed(0)} k`} t="—" ok />
          <PerfCard k="OPEX (£/yr)"          v={`£ ${(annual.perDevice.opex_GBPperYear / 1000).toFixed(1)} k`} t="—" ok />
          <PerfCard k="Manifold ΔP"          v={`${annual.perDevice.manifoldPressureBar.toFixed(2)} bar`} t="< 1.5" ok />
          <PerfCard k="Devices placed"       v={`${placedDevices.length}`} t={`= ${spec.count}`}
            ok={placedDevices.length === spec.count} />
        </div>
      </section>

      {/* ─────────── Bathymetric placement ─────────── */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-amber-300" /> Auto-placement on Barcombe bathymetry
        </div>
        <div className="text-xs text-slate-400 mb-2">
          {placedDevices.length} device{placedDevices.length === 1 ? '' : 's'} positioned by the placement engine: deep-zone bias = {spec.deepZoneBias.toFixed(1)}, minimum spacing = {spec.spacingFactor.toFixed(1)} × plume Ø. {placedDevices.filter(d => d.inDeepZone).length} of {placedDevices.length} sit in the deepest 30 % zone.
        </div>
        <BathymetryMap
          bathymetry={BARCOMBE_BATH}
          depthMax={8}
          siteName="Barcombe Reservoir"
          initialModules={placedForMap}
          width={720}
        />
      </section>

      {/* ─────────── Seasonal health drill-down ─────────── */}
      <section className="space-y-3">
        <div className="text-base font-bold text-slate-100 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-fuchsia-300" /> Seasonal health drill-down
        </div>
        {annual.seasons.map(r => (
          <details key={r.season} className="rounded-2xl border border-slate-700 bg-slate-900">
            <summary className="cursor-pointer px-4 py-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-sky-300" />
                <span className="text-sm font-bold text-slate-100">{SEASON_LABEL[r.season]}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">
                  T = {r.inputs.surfaceTempC.toFixed(0)} °C · DO_hypo = {r.inputs.doHypo_mgL.toFixed(1)} mg/L · {r.inputs.stratified ? 'stratified' : 'mixed'}
                </span>
                <span className="text-xl font-bold"
                  style={{ color: r.health.overallStatus === 'ok' ? '#34d399' : r.health.overallStatus === 'warn' ? '#fbbf24' : '#f87171' }}>
                  {r.health.overallScore.toFixed(0)}
                </span>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); loadToBarcombe(r.season); }}
                  className="text-xs rounded-md bg-sky-600 hover:bg-sky-500 text-white px-2 py-0.5">
                  Load → simulator
                </button>
              </div>
            </summary>
            <div className="px-4 pb-4">
              <ReservoirHealthScorecard params={r.inputs} scenarioName={`Barcombe ${SEASON_LABEL[r.season]} with Perfect Diffuser`} />
            </div>
          </details>
        ))}
      </section>

      {/* ─────────── Adjustment feedback ─────────── */}
      <section className="rounded-2xl border-2 border-amber-700/60 bg-amber-950/20 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-300" /> Adjustment feedback — what to tune next
        </div>
        <p className="text-xs text-slate-400 mb-3">
          The auto-feedback engine perturbs each design parameter, re-runs the annual evaluation, and reports the expected
          annual-score gain. Apply suggestions one at a time and re-evaluate.
        </p>
        {adjustments.length === 0 ? (
          <div className="text-sm text-emerald-200 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> No adjustment produces a material improvement — current spec is locally optimal across the four seasons.
          </div>
        ) : (
          <ol className="space-y-2">
            {adjustments.map((a, i) => (
              <li key={i} className="rounded-lg border border-amber-700/40 bg-slate-900 p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-100">
                      <span className="text-amber-300">#{i + 1}</span> · {a.rationale}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      <code className="text-amber-300">{a.parameter}</code>: {a.currentValue} → {a.suggestedValue}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Affects: {a.affects.join(' · ')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-300">+{a.expectedScoreGain.toFixed(1)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">annual gain</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function SliderField({ label, unit, min, max, step, value, onChange, help }: {
  label: string; unit: string; min: number; max: number; step: number;
  value: number; onChange: (v: number) => void; help?: string;
}) {
  return (
    <div>
      <label className="text-xs text-slate-400 block">
        {label}: <strong className="text-slate-100">{Number.isInteger(step) ? value : value.toFixed(2)} {unit}</strong>
      </label>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-sky-500" />
      {help && <div className="text-[10px] text-slate-500 italic">{help}</div>}
    </div>
  );
}

function SelectField<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void;
}) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value as T)}
        className="w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-200">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function PerfCard({ k, v, t, ok }: { k: string; v: string; t: string; ok: boolean }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'border-emerald-700/60 bg-emerald-900/15' : 'border-amber-700/60 bg-amber-900/15'}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
        <Icon className={`w-3 h-3 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} /> {k}
      </div>
      <div className={`text-lg font-bold mt-1 ${ok ? 'text-emerald-200' : 'text-amber-200'}`}>{v}</div>
      {t !== '—' && <div className="text-[10px] text-slate-500">target {t}</div>}
    </div>
  );
}

// Unused-import guard
export const _icons = [ZapOff, Zap, XCircle];
